import API_BASE_URL from './config';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';

function KennelProfile() {
  const { kennelId } = useParams();
  const [kennel, setKennel] = useState(null);
  const [dogs, setDogs] = useState([]);
  const [staff, setStaff] = useState([]);
  const [message, setMessage] = useState('');
  const [playerId, setPlayerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availableDogs, setAvailableDogs] = useState([]);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [selectedDogId, setSelectedDogId] = useState('');
  const [selectedRemoveDogId, setSelectedRemoveDogId] = useState('');
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [selectedRemoveStaffId, setSelectedRemoveStaffId] = useState('');
  const [showManageOptions, setShowManageOptions] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newMapUrl, setNewMapUrl] = useState('');
  const [isMapOpen, setIsMapOpen] = useState(false);
  const [inventory, setInventory] = useState([]); // Player's inventory for kennel movement
  const [selectedZone, setSelectedZone] = useState(''); // Selected zone for movement

  const fetchKennel = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/kennel/${kennelId}`);
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(`HTTP error! Status: ${res.status} - ${errorData.message || 'Unknown error'}`);
      }
      const data = await res.json();
      console.log('Fetched kennel data:', JSON.stringify(data, null, 2));
      setKennel(data.kennel || {});
      setDogs(Array.isArray(data.dogs) ? data.dogs : []);
      setStaff(Array.isArray(data.staff) ? data.staff : []);
    } catch (err) {
      console.error('Fetch kennel error:', err.message);
      setMessage('Failed to fetch kennel: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [kennelId, setKennel, setDogs, setStaff, setLoading, setMessage]);

  useEffect(() => {
    const storedPlayerId = localStorage.getItem('playerId');
    if (storedPlayerId) setPlayerId(storedPlayerId);
    else setMessage('Please log in to view this page.');
  }, []);

  useEffect(() => {
    if (!playerId) return;

    const fetchAvailableDogs = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/my-dogs/${playerId}`);
        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(`HTTP error! Status: ${res.status} - ${errorData.message || 'Unknown error'}`);
        }
        const dogs = await res.json();
        const unassignedDogs = (dogs || []).filter(d => d.status === 'Approved' && !d.kennelId);
        setAvailableDogs(unassignedDogs);
      } catch (error) {
        setMessage('Failed to fetch available dogs: ' + error.message);
      }
    };

    const fetchAvailableStaff = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/my-staff/${playerId}`);
        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(`HTTP error! Status: ${res.status} - ${errorData.message || 'Unknown error'}`);
        }
        const staff = await res.json();
        const approvedStaff = (staff || []).filter(s => s.status === 'Approved');
        setAvailableStaff(approvedStaff);
      } catch (error) {
        setMessage('Failed to fetch available staff: ' + error.message);
      }
    };

    const fetchInventory = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/my-inventory/${playerId}`);
        if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);
        const data = await res.json();
        setInventory(data.items || []);
      } catch (error) {
        setMessage('Failed to fetch inventory: ' + error.message);
      }
    };

    fetchKennel();
    fetchAvailableDogs();
    fetchAvailableStaff();
    fetchInventory();
  }, [kennelId, playerId, fetchKennel]);

  const updateImage = async () => {
    if (!newImageUrl) {
      setMessage('Please enter a new image URL.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/kennel/${kennelId}/update-image`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, artUrl: newImageUrl })
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(`HTTP error! Status: ${res.status} - ${errorData.message || 'Unknown error'}`);
      }
      const data = await res.json();
      setKennel(data.kennel);
      setMessage(data.message);
      setNewImageUrl('');
      setShowManageOptions(false);
    } catch (error) {
      setMessage('Failed to request image update: ' + error.message);
    }
  };

  const updateMap = async () => {
    if (!newMapUrl) {
      setMessage('Please enter a kennel map URL.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/kennel/${kennelId}/update-map`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, kennelMapUrl: newMapUrl })
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(`HTTP error! Status: ${res.status} - ${errorData.message || 'Unknown error'}`);
      }
      const data = await res.json();
      setKennel(data.kennel);
      setNewMapUrl('');
      setMessage(data.message);
    } catch (error) {
      setMessage('Error updating map: ' + error.message);
    }
  };

  const clearMap = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/kennel/${kennelId}/update-map`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, kennelMapUrl: '' })
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(`HTTP error! Status: ${res.status} - ${errorData.message || 'Unknown error'}`);
      }
      const data = await res.json();
      setKennel(data.kennel);
      setMessage('Kennel map cleared');
    } catch (error) {
      setMessage('Error clearing map: ' + error.message);
    }
  };

  const retireKennel = async () => {
    if (!window.confirm('Are you sure you want to retire this kennel? This action cannot be undone.')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/kennel/${kennelId}/retire`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId })
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(`HTTP error! Status: ${res.status} - ${errorData.message || 'Unknown error'}`);
      }
      const data = await res.json();
      setKennel(data.kennel);
      setMessage(data.message);
    } catch (error) {
      setMessage('Failed to retire kennel: ' + error.message);
    }
  };

  const assignDog = async () => {
    if (!selectedDogId) {
      setMessage('Please select a dog to assign.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/dog/${selectedDogId}/assign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kennelId, playerId })
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(`HTTP error! Status: ${res.status} - ${errorData.message || 'Unknown error'}`);
      }
      const data = await res.json();
      setMessage(data.message);
      const assignedDog = availableDogs.find(d => d._id === selectedDogId);
      setDogs([...dogs, assignedDog]);
      setAvailableDogs(availableDogs.filter(d => d._id !== selectedDogId));
      setSelectedDogId('');
      await fetchKennel(); // Refresh kennel data
    } catch (error) {
      setMessage('Failed to assign dog: ' + error.message);
    }
  };

  const removeDog = async () => {
    if (!selectedRemoveDogId) {
      setMessage('Please select a dog to remove.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/kennel/${kennelId}/remove-dog`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, dogId: selectedRemoveDogId })
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(`HTTP error! Status: ${res.status} - ${errorData.message || 'Unknown error'}`);
      }
      const data = await res.json();
      setKennel(data.kennel);
      const removedDog = dogs.find(d => d._id === selectedRemoveDogId);
      setDogs(dogs.filter(d => d._id !== selectedRemoveDogId));
      if (removedDog) setAvailableDogs([...availableDogs, removedDog]);
      setMessage(data.message);
      setSelectedRemoveDogId('');
      await fetchKennel(); // Refresh kennel data
    } catch (error) {
      setMessage('Error removing dog: ' + error.message);
    }
  };

  const assignStaff = async () => {
    if (!selectedStaffId) {
      setMessage('Please select a staff member to assign.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/kennel/${kennelId}/assign-staff`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, staffId: selectedStaffId })
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(`HTTP error! Status: ${res.status} - ${errorData.message || 'Unknown error'}`);
      }
      const data = await res.json();
      setMessage(data.message);
      const assignedStaff = availableStaff.find(s => s._id === selectedStaffId);
      setStaff([...staff, assignedStaff]);
      setAvailableStaff(availableStaff.filter(s => s._id !== selectedStaffId));
      setSelectedStaffId('');
      await fetchKennel(); // Refresh kennel data
    } catch (error) {
      setMessage('Failed to assign staff: ' + error.message);
    }
  };

  const removeStaff = async () => {
    if (!selectedRemoveStaffId) {
      setMessage('Please select a staff member to remove.');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/kennel/${kennelId}/remove-staff`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, staffId: selectedRemoveStaffId })
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(`HTTP error! Status: ${res.status} - ${errorData.message || 'Unknown error'}`);
      }
      const data = await res.json();
      setKennel(data.kennel);
      setStaff(staff.filter(s => s._id !== selectedRemoveStaffId));
      setMessage(data.message);
      setSelectedRemoveStaffId('');
      await fetchKennel(); // Refresh kennel data
    } catch (error) {
      setMessage('Error removing staff: ' + error.message);
    }
  };

  const moveKennel = async () => {
    const newRegionId = parseInt(selectedZone, 10);
    if (!newRegionId) {
      setMessage('Please select a zone to move to.');
      return;
    }

    // Define required items for each zone (for frontend validation)
    const requiredItems = {
      1: 'kmv_travel_docs',
      2: 'kmv_travel_docs',
      3: 'kmv_travel_docs',
      4: 'kmv_travel_docs',
      5: 'kmv_travel_docs',
      6: 'kmv_z6_key',
      7: 'kmv_z7_pass',
      8: 'kmv_z8_shard',
      9: 'kmv_z9_core',
      10: 'kmv_z10_anchor'
    };

    const requiredItemId = requiredItems[newRegionId];
    if (!requiredItemId) {
      setMessage('No item required for this zone or zone not supported.');
      return;
    }

    const itemId = requiredItemId; // Send the primary item ID (backend handles multi-item check)

    try {
      const res = await fetch(`${API_BASE_URL}/kennel/${kennelId}/move`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, newRegionId, itemId })
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(`HTTP error! Status: ${res.status} - ${errorData.error || 'Unknown error'}${errorData.details ? `: ${errorData.details}` : ''}`);
      }
      const data = await res.json();
      setMessage(data.message);
      setKennel(data.kennel);
      setInventory(inventory.map(item => {
        const reqItem = (Array.isArray(requiredItems[newRegionId]) ? requiredItems[newRegionId] : [requiredItems[newRegionId]])
          .find(r => r.itemId === item.itemId);
        return reqItem && item.quantity > reqItem.quantity
          ? { ...item, quantity: item.quantity - reqItem.quantity }
          : item;
      }).filter(item => item.quantity > 0));
      setSelectedZone('');
    } catch (error) {
      setMessage('Failed to move kennel: ' + error.message);
    }
  };

  if (message && !kennel) return <p>{message}</p>;
  if (loading || !kennel) return <p style={{ textAlign: 'center', color: '#888' }}>Loading...</p>;

  const maleDogs = dogs.filter(dog => dog.gender === 'Male');
  const femaleDogs = dogs.filter(dog => dog.gender === 'Female');

  const zoneNames = {
    1: 'USA, Canada, and Mexico',
    2: 'Europe',
    3: 'Asia, Oceania, and Australia',
    4: 'Africa and the Middle East',
    5: 'South America',
    6: 'Antarctica',
    7: 'North Pole',
    8: 'The Arcane Vale (Fantasy Realm - LOCKED)',
    9: 'Intergalactic Starbase (Fantasy Realm - LOCKED)',
    10: 'The Temporal Shift (Fantasy Realm - LOCKED)',
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center', paddingTop: '60px' }}>
      {message && (
        <div style={{
          backgroundColor: '#90EE90',
          padding: '10px',
          marginBottom: '20px',
          borderRadius: '5px',
          color: 'black',
          fontWeight: 'bold',
          textAlign: 'center'
        }}>
          {message}
        </div>
      )}
      {kennel.retired && (
        <div style={{
          backgroundColor: '#555',
          color: 'white',
          padding: '15px',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: '1.2em',
          marginBottom: '20px',
          borderRadius: '5px'
        }}>
          Retired
        </div>
      )}
      <h1 style={{ color: '#1E88E5', fontSize: '1.8em', margin: '0 0 10px' }}>{kennel.kennelName}</h1>
      {kennel.artUrl ? (
        <img src={kennel.artUrl} alt={kennel.kennelName} style={{ width: '300px', height: '300px', objectFit: 'cover', marginBottom: '10px', borderRadius: '8px' }} />
      ) : (
        <div style={{ width: '300px', height: '300px', backgroundColor: '#ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10px', borderRadius: '8px' }}>
          No Image
        </div>
      )}
      <p style={{ color: '#555', fontSize: '1.1em' }}>Current Zone: {kennel.regionId} - {zoneNames[kennel.regionId]}</p>

      {/* Collapsible Kennel Map Section */}
      <div style={{ marginTop: '20px' }}>
        <button
          onClick={() => setIsMapOpen(!isMapOpen)}
          style={{
            padding: '8px 15px',
            backgroundColor: '#1E88E5',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            transition: 'background-color 0.3s'
          }}
          onMouseOver={e => e.target.style.backgroundColor = '#1976D2'}
          onMouseOut={e => e.target.style.backgroundColor = '#1E88E5'}
        >
          {isMapOpen ? 'Hide Kennel Map' : 'Show Kennel Map'}
        </button>
        {isMapOpen && (
          <div style={{ marginTop: '10px' }}>
            {kennel.kennelMapUrl ? (
              <img src={kennel.kennelMapUrl} alt="Kennel Map" style={{ width: '300px', height: '300px', objectFit: 'cover', borderRadius: '8px' }} />
            ) : (
              <p>No kennel map set.</p>
            )}
          </div>
        )}
      </div>

      {playerId === kennel.playerId && !kennel.retired && (
        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={() => setShowManageOptions(!showManageOptions)}
            style={{
              padding: '6px 12px',
              backgroundColor: '#f5f5f5',
              color: '#888',
              border: '1px solid #ddd',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '0.9em',
              transition: 'color 0.3s, border-color 0.3s'
            }}
            onMouseOver={e => {
              e.target.style.color = '#1E88E5';
              e.target.style.borderColor = '#1E88E5';
            }}
            onMouseOut={e => {
              e.target.style.color = '#888';
              e.target.style.borderColor = '#ddd';
            }}
          >
            Manage Kennel
          </button>
          {showManageOptions && (
            <div style={{
              marginTop: '10px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '10px'
            }}>
              {!kennel.pendingArtUrl && (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="url"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    placeholder="New image URL"
                    style={{
                      padding: '6px',
                      borderRadius: '5px',
                      border: '1px solid #BBDEFB',
                      fontSize: '0.9em',
                      width: '200px'
                    }}
                  />
                  <button
                    onClick={updateImage}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#f5f5f5',
                      color: '#888',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                      transition: 'color 0.3s, border-color 0.3s'
                    }}
                    onMouseOver={e => {
                      e.target.style.color = '#4CAF50';
                      e.target.style.borderColor = '#4CAF50';
                    }}
                    onMouseOut={e => {
                      e.target.style.color = '#888';
                      e.target.style.borderColor = '#ddd';
                    }}
                  >
                    Update Image
                  </button>
                </div>
              )}
              {!kennel.kennelMapUrl ? (
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="url"
                    value={newMapUrl}
                    onChange={(e) => setNewMapUrl(e.target.value)}
                    placeholder="New kennel map URL"
                    style={{
                      padding: '6px',
                      borderRadius: '5px',
                      border: '1px solid #BBDEFB',
                      fontSize: '0.9em',
                      width: '200px'
                    }}
                  />
                  <button
                    onClick={updateMap}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#f5f5f5',
                      color: '#888',
                      border: '1px solid #ddd',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '0.9em',
                      transition: 'color 0.3s, border-color 0.3s'
                    }}
                    onMouseOver={e => {
                      e.target.style.color = '#4CAF50';
                      e.target.style.borderColor = '#4CAF50';
                    }}
                    onMouseOut={e => {
                      e.target.style.color = '#888';
                      e.target.style.borderColor = '#ddd';
                    }}
                  >
                    Add Map
                  </button>
                </div>
              ) : (
                <button
                  onClick={clearMap}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#f5f5f5',
                    color: '#888',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    fontSize: '0.9em',
                    transition: 'color 0.3s, border-color 0.3s'
                  }}
                  onMouseOver={e => {
                    e.target.style.color = '#EF5350';
                    e.target.style.borderColor = '#EF5350';
                  }}
                  onMouseOut={e => {
                    e.target.style.color = '#888';
                    e.target.style.borderColor = '#ddd';
                  }}
                >
                  Clear Map Image
                </button>
              )}
              {/* Assign/Remove Dog Section */}
              <div style={{ marginTop: '20px' }}>
                <h2 style={{ color: '#4CAF50', marginBottom: '10px' }}>Assign/Remove a Dog</h2>
                <div style={{ marginBottom: '15px' }}>
                  <select
                    value={selectedDogId}
                    onChange={(e) => setSelectedDogId(e.target.value)}
                    style={{
                      padding: '8px',
                      marginBottom: '10px',
                      borderRadius: '5px',
                      border: '1px solid #BBDEFB',
                      width: '300px',
                      fontSize: '1em'
                    }}
                  >
                    <option value="">Select a dog to assign</option>
                    {availableDogs.map(dog => (
                      <option key={dog._id} value={dog._id}>{dog.registeredName} ({dog.dogId})</option>
                    ))}
                  </select>
                  <button
                    onClick={assignDog}
                    style={{
                      padding: '8px 15px',
                      backgroundColor: '#1E88E5',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s'
                    }}
                    onMouseOver={e => e.target.style.backgroundColor = '#1976D2'}
                    onMouseOut={e => e.target.style.backgroundColor = '#1E88E5'}
                  >
                    Assign Dog
                  </button>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <select
                    value={selectedRemoveDogId}
                    onChange={(e) => setSelectedRemoveDogId(e.target.value)}
                    style={{
                      padding: '8px',
                      marginBottom: '10px',
                      borderRadius: '5px',
                      border: '1px solid #BBDEFB',
                      width: '300px',
                      fontSize: '1em'
                    }}
                  >
                    <option value="">Select a dog to remove</option>
                    {dogs.map(dog => (
                      <option key={dog._id} value={dog._id}>{dog.registeredName} ({dog.dogId})</option>
                    ))}
                  </select>
                  <button
                    onClick={removeDog}
                    style={{
                      padding: '8px 15px',
                      backgroundColor: '#EF5350',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s'
                    }}
                    onMouseOver={e => e.target.style.backgroundColor = '#F44336'}
                    onMouseOut={e => e.target.style.backgroundColor = '#EF5350'}
                  >
                    Remove Dog
                  </button>
                </div>
              </div>
              {/* Assign/Remove Staff Section */}
              <div style={{ marginTop: '20px' }}>
                <h2 style={{ color: '#4CAF50', marginBottom: '10px' }}>Assign/Remove Staff</h2>
                <div style={{ marginBottom: '15px' }}>
                  <select
                    value={selectedStaffId}
                    onChange={(e) => setSelectedStaffId(e.target.value)}
                    style={{
                      padding: '8px',
                      marginBottom: '10px',
                      borderRadius: '5px',
                      border: '1px solid #BBDEFB',
                      width: '300px',
                      fontSize: '1em'
                    }}
                  >
                    <option value="">Select a staff member to assign</option>
                    {availableStaff.map(s => (
                      <option key={s._id} value={s._id}>{s.staffName}</option>
                    ))}
                  </select>
                  <button
                    onClick={assignStaff}
                    style={{
                      padding: '8px 15px',
                      backgroundColor: '#1E88E5',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s'
                    }}
                    onMouseOver={e => e.target.style.backgroundColor = '#1976D2'}
                    onMouseOut={e => e.target.style.backgroundColor = '#1E88E5'}
                  >
                    Assign Staff
                  </button>
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <select
                    value={selectedRemoveStaffId}
                    onChange={(e) => setSelectedRemoveStaffId(e.target.value)}
                    style={{
                      padding: '8px',
                      marginBottom: '10px',
                      borderRadius: '5px',
                      border: '1px solid #BBDEFB',
                      width: '300px',
                      fontSize: '1em'
                    }}
                  >
                    <option value="">Select a staff member to remove</option>
                    {staff.map(s => (
                      <option key={s._id} value={s._id}>{s.staffName}</option>
                    ))}
                  </select>
                  <button
                    onClick={removeStaff}
                    style={{
                      padding: '8px 15px',
                      backgroundColor: '#EF5350',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s'
                    }}
                    onMouseOut={e => e.target.style.backgroundColor = '#EF5350'}
                  >
                    Remove Staff
                  </button>
                </div>
              </div>
              {/* Kennel Movement Section */}
              <div style={{ marginTop: '20px' }}>
                <h2 style={{ color: '#4CAF50', marginBottom: '10px' }}>Move Kennel</h2>
                <select
                  value={selectedZone}
                  onChange={(e) => setSelectedZone(e.target.value)}
                  style={{
                    padding: '8px',
                    marginBottom: '10px',
                    borderRadius: '5px',
                    border: '1px solid #BBDEFB',
                    width: '300px',
                    fontSize: '1em'
                  }}
                >
                  <option value="">Select a new zone</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(zone => (
                    <option key={zone} value={zone}>Zone {zone} - {zoneNames[zone]}</option>
                  ))}
                </select>
                <button
                  onClick={moveKennel}
                  style={{
                    padding: '8px 15px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s'
                  }}
                  onMouseOver={e => e.target.style.backgroundColor = '#45A049'}
                  onMouseOut={e => e.target.style.backgroundColor = '#4CAF50'}
                >
                  Move Kennel
                </button>
              </div>
              <button
                onClick={retireKennel}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#f5f5f5',
                  color: '#888',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                  transition: 'color 0.3s, border-color 0.3s'
                }}
                onMouseOver={e => {
                  e.target.style.color = '#EF5350';
                  e.target.style.borderColor = '#EF5350';
                }}
                onMouseOut={e => {
                  e.target.style.color = '#888';
                  e.target.style.borderColor = '#ddd';
                }}
              >
                Retire This Kennel
              </button>
            </div>
          )}
        </div>
      )}

      {kennel.stashLink && (
        <p><strong>Stash Link:</strong> <a href={kennel.stashLink} target="_blank" rel="noopener noreferrer" style={{ color: '#4CAF50' }}>{kennel.stashLink}</a></p>
      )}

      <h2 style={{ color: '#4CAF50', marginBottom: '20px' }}>Dogs</h2>
      <div style={{
        backgroundColor: '#f9f9f9',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        border: '1px solid #d9ead3',
        display: 'flex',
        justifyContent: 'space-between',
        maxWidth: '800px',
        margin: '0 auto 20px'
      }}>
        <div style={{ width: '45%' }}>
          <h3 style={{ color: '#0b5394', marginBottom: '10px' }}>Males</h3>
          {maleDogs.length > 0 ? (
            <ul style={{ listStyleType: 'none', padding: 0, textAlign: 'left' }}>
              {maleDogs.map(dog => (
                <li key={dog.dogId} style={{ margin: '5px 0' }}>
                  <Link to={`/dog/${dog.dogId}`} style={{ color: '#4CAF50', textDecoration: 'none' }}>{dog.registeredName}</Link>
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ color: '#888' }}>No male dogs in this kennel.</p>
          )}
        </div>
        <div style={{ width: '45%' }}>
          <h3 style={{ color: '#0b5394', marginBottom: '10px' }}>Females</h3>
          {femaleDogs.length > 0 ? (
            <ul style={{ listStyleType: 'none', padding: 0, textAlign: 'left' }}>
              {femaleDogs.map(dog => (
                <li key={dog.dogId} style={{ margin: '5px 0' }}>
                  <Link to={`/dog/${dog.dogId}`} style={{ color: '#4CAF50', textDecoration: 'none' }}>{dog.registeredName}</Link>
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ color: '#888' }}>No female dogs in this kennel.</p>
          )}
        </div>
      </div>

      <h2 style={{ color: '#4CAF50', marginBottom: '20px' }}>Staff</h2>
      <div style={{
        backgroundColor: '#f9f9f9',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
        border: '1px solid #d9ead3'
      }}>
        {staff.length > 0 ? (
          <ul style={{ listStyleType: 'none', padding: '0', textAlign: 'left' }}>
            {staff.map(s => (
              <li key={s._id} style={{ margin: '5px 0' }}>
                <Link to={`/staff/${s._id}`} style={{ color: '#4CAF50', textDecoration: 'none' }}>{s.staffName}</Link>
              </li>
            ))}
          </ul>
        ) : (
          <p style={{ color: '#888' }}>No staff employed at this kennel.</p>
        )}
      </div>
    </div>
  );
}

export default KennelProfile;